// src/components/atoms/menu/MenuAtom.tsx

import React, { forwardRef } from 'react';
import { Menu } from 'primereact/menu';
import './menu.scss';

/**
 * Interface for MenuItem based on PrimeReact's documentation.
 */
export interface MenuItem {
  label?: string;
  icon?: string;
  command?: (event: any) => void;
  url?: string;
  items?: MenuItem[] | MenuItem[][];
  expanded?: boolean;
  disabled?: boolean;
  visible?: boolean;
  target?: string;
  separator?: boolean;
  style?: React.CSSProperties;
  className?: string;
  template?: React.ReactNode | ((item: MenuItem) => React.ReactNode);
  data?: any;
  /** Present → a checkable item (`role="menuitemcheckbox"`, `aria-checked`) with a check glyph when true. */
  checked?: boolean;
}

export interface MenuHandle {
  toggle(event: React.SyntheticEvent<Element, Event>): void;
  show(event: React.SyntheticEvent<Element, Event>): void;
  hide(event: React.SyntheticEvent<Element, Event>): void;
  getElement(): HTMLDivElement | null;
  getTarget(): null | EventTarget;
}

/**
 * Props for the MenuAtom component.
 */
interface MenuAtomProps {
  model: MenuItem[];
  popup?: boolean;
  menuRef?: React.Ref<MenuHandle>;
  /** Id of the popup element, for a trigger's `aria-controls`. */
  id?: string;
  appendTo?: HTMLElement | 'self' | (() => HTMLElement) | null;
  className?: string;
  onShow?: (event: React.SyntheticEvent<Element, Event>) => void;  // PrimeReact callback
  onHide?: (event: React.SyntheticEvent<Element, Event>) => void;  // PrimeReact callback
  // any other props you might want to debug...
}

/**
 * MenuAtom Component
 *
 * A custom atom encapsulating PrimeReact's Menu, ensuring it aligns with
 * PrimeReact's documented method signatures for toggle/show/hide.
 */
const withCheckGlyph = (item: MenuItem): MenuItem => {
  if (item.checked === undefined || item.template) return item;
  return {
    ...item,
    // No anchor inside: a menuitemcheckbox's children are presentational; the <li> owns click and keys.
    template: () => (
      <div className="atlas-menu-checkable p-menuitem-content">
        <span className="p-menuitem-link">
          {item.icon && <span className={`p-menuitem-icon ${item.icon}`} aria-hidden="true" />}
          <span className="p-menuitem-text">{item.label}</span>
        </span>
        <span
          className={`atlas-menu-check pi pi-check${item.checked ? '' : ' atlas-menu-check--off'}`}
          aria-hidden="true"
        />
      </div>
    ),
  } as MenuItem;
};

const checkablePassThrough = {
  menuitem: (options: { context?: { item?: MenuItem } }) => {
    const checked = options.context?.item?.checked;
    return checked === undefined
      ? {}
      : { role: 'menuitemcheckbox', 'aria-checked': checked };
  },
};

const MenuAtom = forwardRef<MenuHandle, MenuAtomProps>(
  (
    { model, popup = false, appendTo = 'self', className, onShow, onHide, ...restProps },
    ref
  ) => {

    return (
      <Menu
        model={model.map(withCheckGlyph) as any}         // Casting away TS mismatch
        pt={checkablePassThrough as any}
        popup={popup}
        appendTo={appendTo}
        className={className}
        baseZIndex={999999}
        onShow={(e) => {
          onShow?.(e);
        }}
        onHide={(e) => {
          onHide?.(e);
        }}
        // Note: The PrimeReact <Menu> internally has
        // toggle(event: SyntheticEvent) => void, etc.
        // We'll align the ref type using "any" to avoid TS conflicts.
        ref={ref as React.RefObject<any>}
        {...restProps}
      />
    );
  }
);

export default MenuAtom;
